import { motion, useTransform } from 'framer-motion';
import { Scene, useSpan } from '../motion';
import { LEDGER, TRANSFERS, formatMoney } from '../demoData';

// Winners across the top, losers across the bottom.
const ANGLES = { You: 210, Jun: 270, Priya: 330, Marisol: 150, Lena: 90, Theo: 30 };
const C = 200;
const R = 148;
const pos = name => {
  const a = (ANGLES[name] * Math.PI) / 180;
  return { x: C + R * Math.cos(a), y: C + R * Math.sin(a) };
};
const NODE_R = 30;

// Shorten a segment so it starts and ends at the node edges.
function edge(from, to, pad = NODE_R + 6) {
  const a = pos(from);
  const b = pos(to);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  return { x1: a.x + ux * pad, y1: a.y + uy * pad, x2: b.x - ux * pad, y2: b.y - uy * pad };
}

const creditors = LEDGER.filter(p => p.net > 0);
const debtors = LEDGER.filter(p => p.net < 0);
const IOUS = debtors.flatMap(d => creditors.map(c => ({ from: d.name, to: c.name })));

const IOU_IN = [0.05, 0.3];
const IOU_OUT = [0.38, 0.5];
const T_START = 0.42;
const T_STEP = 0.075;
const tAt = i => [T_START + i * T_STEP, T_START + i * T_STEP + 0.1];

function Iou({ p, iou, i }) {
  const a = IOU_IN[0] + (i / IOUS.length) * (IOU_IN[1] - IOU_IN[0]);
  const inT = useSpan(p, a, a + 0.06);
  const outT = useSpan(p, IOU_OUT[0], IOU_OUT[1]);
  const opacity = useTransform([inT, outT], ([x, y]) => x * (1 - y) * 0.7);
  const e = edge(iou.from, iou.to);
  return (
    <motion.line {...e} stroke="rgb(var(--pn-fg))" strokeWidth="1.2" strokeDasharray="3 5" style={{ opacity }} />
  );
}

function Transfer({ p, t, i }) {
  const [a, b] = tAt(i);
  const pathLength = useSpan(p, a, b);
  const label = useSpan(p, b - 0.03, b + 0.02);
  const e = edge(t.from, t.to);
  // Label sits a third of the way from the payer so crossing lines don't collide.
  const mx = e.x1 + (e.x2 - e.x1) * 0.36;
  const my = e.y1 + (e.y2 - e.y1) * 0.36;
  const angle = (Math.atan2(e.y2 - e.y1, e.x2 - e.x1) * 180) / Math.PI;
  return (
    <g>
      <motion.line {...e} stroke="rgb(var(--pn-signal))" strokeWidth="2.5" strokeLinecap="round" style={{ pathLength }} />
      <motion.path
        d="M0 0L-9 -5L-9 5Z"
        fill="rgb(var(--pn-signal))"
        transform={`translate(${e.x2} ${e.y2}) rotate(${angle})`}
        style={{ opacity: label }}
      />
      <motion.g style={{ opacity: label }}>
        <rect x={mx - 30} y={my - 11} width="60" height="22" rx="11" fill="rgb(var(--pn-raised))" stroke="rgb(var(--pn-signal) / 0.5)" />
        <text x={mx} y={my + 4.5} textAnchor="middle" className="pn-num" fontSize="12" fill="rgb(var(--pn-fg))">
          {t.amount.toFixed(2)}
        </text>
      </motion.g>
    </g>
  );
}

function Node({ player }) {
  const { x, y } = pos(player.name);
  const up = player.net > 0;
  return (
    <g>
      <circle cx={x} cy={y} r={NODE_R} fill="rgb(var(--pn-raised))" stroke={up ? 'rgb(var(--pn-signal) / 0.6)' : 'rgb(var(--pn-loss) / 0.5)'} strokeWidth="1.5" />
      <text x={x} y={y - 2} textAnchor="middle" fontSize="13" fontWeight="700" fill="rgb(var(--pn-fg))">{player.name}</text>
      <text x={x} y={y + 13} textAnchor="middle" fontSize="10.5" className="pn-num" fill={up ? 'rgb(var(--pn-signal))' : 'rgb(var(--pn-loss))'}>
        {formatMoney(player.net, { sign: true })}
      </text>
    </g>
  );
}

function TransferRow({ p, t, i }) {
  const [, b] = tAt(i);
  const s = useSpan(p, b - 0.04, b + 0.02);
  const x = useTransform(s, [0, 1], [16, 0]);
  return (
    <motion.li style={{ opacity: s, x }} className="flex items-center justify-between gap-4 py-[clamp(0.4rem,0.2rem+0.6*var(--u),0.8rem)]">
      <span className="text-[clamp(0.9rem,0.7rem+0.6*var(--u),1.2rem)]">
        <b>{t.from}</b> <span className="text-pn-muted">pays</span> <b>{t.to}</b>
      </span>
      <span className="pn-num text-[clamp(0.9rem,0.7rem+0.6*var(--u),1.2rem)]">{formatMoney(t.amount)}</span>
    </motion.li>
  );
}

export default function SettleUp() {
  return (
    <Scene id="ledger" length={2.6} label="Settle up">
      {p => <Frame p={p} />}
    </Scene>
  );
}

function Frame({ p }) {
  const iouCount = useSpan(p, IOU_IN[0], IOU_IN[1]);
  const phase = useSpan(p, IOU_OUT[0], IOU_OUT[1]);
  const share = useSpan(p, 0.84, 0.92);
  const status = useTransform([iouCount, phase], ([a, b]) =>
    b > 0.5 ? `${TRANSFERS.length} transfers` : `${Math.round(a * IOUS.length)} IOUs`);

  return (
    <div className="mx-auto flex h-full max-w-[1480px] flex-col justify-center px-4 pb-6 pt-20 sm:px-6 tall:px-12 land:grid land:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] land:items-center land:gap-16">
      <div>
        <h2 className="pn-h2">Nine IOUs. Five transfers.</h2>
        <p className="pn-lede mt-4">
          Upload the ledger, merge duplicate names, and get the shortest way for everyone to get paid.
        </p>
        <ul className="mt-6 hidden divide-y divide-pn-line/10 border-y border-pn-line/10 land:block tall:block">
          {TRANSFERS.map((t, i) => <TransferRow key={i} p={p} t={t} i={i} />)}
        </ul>
        <motion.p style={{ opacity: share }} className="pn-caption mt-5 hidden land:block tall:block">
          Saved and shared at pokernowai.com/shared-ledger/thursday
        </motion.p>
      </div>

      <div className="relative mx-auto mt-4 w-full max-w-[min(100%,62vh)] land:mt-0 tall:max-w-[min(100%,46vh)]">
        <svg viewBox="0 0 400 400" className="h-auto w-full overflow-visible" role="img" aria-label="Settle-up graph: Lena pays Jun 79.20, Theo pays Jun 7.20, Theo pays Priya 41.15, Theo pays You 15.45, Marisol pays You 12.25">
          {IOUS.map((iou, i) => <Iou key={i} p={p} iou={iou} i={i} />)}
          {TRANSFERS.map((t, i) => <Transfer key={i} p={p} t={t} i={i} />)}
          {LEDGER.map(player => <Node key={player.name} player={player} />)}
        </svg>
        <motion.p className="pn-num mt-2 text-center text-[clamp(0.85rem,0.6rem+0.8*var(--u),1.3rem)] text-pn-muted">
          {status}
        </motion.p>
      </div>
    </div>
  );
}
